import { defaultHandoverAgent, type HandoverAgent } from '@acasa/shared'
import { useEffect, useState } from 'react'
import { Card } from '@/components/card'
import { useSession } from '@/features/auth/use-auth'
import { Field, TextInput } from '@/features/handovers/fields'
import { ApiRequestError } from '@/lib/api'
import { useAppSettings, useUpdateAppSettings } from './use-app-settings'

/**
 * The agency's Makler, as printed on a handover protocol.
 *
 * Every protocol has a Makler party, and until this existed it arrived with a
 * name and nothing else — address, telephone number and e-mail were typed in
 * again at every appointment, or left out. They are the same every time, so
 * they are kept here once, per person.
 *
 * A new protocol gets the entry whose name is the object's Betreuer in
 * onOffice, else the Standard; in the protocol any entry can be picked. An
 * empty list means the Betreuer's name, as before the setting existed.
 *
 * Admin only, like the rest of the settings; the API answers 403 otherwise.
 */

type Agent = HandoverAgent

/** Mirrors the server's check, so a typo reads as a sentence and not as a 400. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * The list as it would be stored: trimmed, with exactly one Standard. With
 * none flagged the server counts the first, so the draft does too — and
 * comparing two normalised lists is what keeps "nothing flagged" and "first
 * flagged" from reading as an unsaved change.
 */
function normalise(agents: readonly Agent[]): Agent[] {
  const fallback = defaultHandoverAgent(agents)?.id
  return agents.map((agent) => ({
    id: agent.id,
    name: agent.name.trim(),
    address: agent.address.trim(),
    phone: agent.phone.trim(),
    email: agent.email.trim(),
    isDefault: agent.id === fallback,
  }))
}

function problemOf(agent: Agent): string | null {
  if (agent.name.trim() === '') return 'Name fehlt.'
  if (agent.email.trim() !== '' && !EMAIL.test(agent.email.trim())) {
    return 'Keine gültige E-Mail-Adresse.'
  }
  return null
}

const blankAgent = (isDefault: boolean): Agent => ({
  id: crypto.randomUUID(),
  name: '',
  address: '',
  phone: '',
  email: '',
  isDefault,
})

export function HandoverSettingsCard() {
  const { user } = useSession()
  const isAdmin = user?.role === 'admin'

  const query = useAppSettings(isAdmin)
  const save = useUpdateAppSettings()

  const server = query.data?.settings.handovers.agents ?? null
  const [draft, setDraft] = useState<Agent[] | null>(null)
  const [saved, setSaved] = useState(false)

  // Structural sharing keeps `agents` the same array unless ITS values change,
  // so a save in another card does not reset what is being typed here.
  useEffect(() => {
    setDraft(server ? server.map((agent) => ({ ...agent })) : null)
  }, [server])

  if (!isAdmin) return null

  const busy = save.isPending
  const problems = draft?.map(problemOf) ?? []
  const invalid = problems.some((problem) => problem !== null)
  const dirty =
    draft !== null &&
    server !== null &&
    JSON.stringify(normalise(draft)) !== JSON.stringify(normalise(server))
  const standardId = draft ? defaultHandoverAgent(draft)?.id : undefined

  const change = (update: (current: Agent[]) => Agent[]) => {
    setDraft((current) => (current ? update(current) : current))
    setSaved(false)
  }

  const edit = (id: string, changes: Partial<Agent>) =>
    change((current) =>
      current.map((agent) => (agent.id === id ? { ...agent, ...changes } : agent)),
    )

  const submit = () => {
    if (!draft || invalid) return
    setSaved(false)
    save.mutate({ handovers: { agents: normalise(draft) } }, { onSuccess: () => setSaved(true) })
  }

  return (
    <Card
      title="Übergabeprotokolle"
      footer="Gilt für neue Protokolle. Bereits angelegte werden nicht verändert, und im Protokoll bleibt jede Angabe änderbar."
    >
      {query.isPending && <p className="text-muted text-sm">Wird geladen …</p>}

      {query.isError && (
        <p className="text-danger text-sm">
          Einstellungen konnten nicht geladen werden: {errorMessage(query.error)}
        </p>
      )}

      {draft && (
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            submit()
          }}
        >
          <div>
            <h3 className="text-ink text-sm font-medium">Makler</h3>
            <p className="text-muted mt-1 text-sm leading-relaxed">
              Ein neues Übergabeprotokoll erhält den Makler, dessen Name dem Betreuer des Objekts in
              onOffice entspricht, sonst den Standard. Im Protokoll kann jeder Makler aus dieser
              Liste gewählt werden. Die Angaben stehen im Protokoll und im PDF.
            </p>
          </div>

          {draft.length === 0 && (
            <p className="text-faint text-sm">
              Keine Makler hinterlegt — der Betreuer des Objekts aus onOffice wird übernommen.
            </p>
          )}

          {draft.map((agent, index) => {
            const problem = problems[index] ?? null
            const nameMissing = agent.name.trim() === ''
            return (
              <fieldset key={agent.id} className="border-line border p-3 sm:p-4" disabled={busy}>
                <legend className="text-faint px-1 text-[10px] tracking-[0.14em] uppercase">
                  Makler {index + 1}
                </legend>

                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Name">
                    <TextInput
                      value={agent.name}
                      maxLength={120}
                      autoComplete="off"
                      placeholder="z. B. Vor- und Nachname, Firma"
                      aria-invalid={nameMissing ? true : undefined}
                      onChange={(event) => edit(agent.id, { name: event.target.value })}
                    />
                  </Field>
                  <Field label="Anschrift">
                    <TextInput
                      value={agent.address}
                      maxLength={300}
                      autoComplete="off"
                      placeholder="Straße Nr., PLZ Ort"
                      onChange={(event) => edit(agent.id, { address: event.target.value })}
                    />
                  </Field>
                  <Field label="Telefon">
                    <TextInput
                      type="tel"
                      inputMode="tel"
                      value={agent.phone}
                      maxLength={60}
                      autoComplete="off"
                      onChange={(event) => edit(agent.id, { phone: event.target.value })}
                    />
                  </Field>
                  <Field label="E-Mail">
                    <TextInput
                      type="email"
                      inputMode="email"
                      value={agent.email}
                      maxLength={200}
                      autoComplete="off"
                      aria-invalid={problem && !nameMissing ? true : undefined}
                      onChange={(event) => edit(agent.id, { email: event.target.value })}
                    />
                  </Field>
                </div>

                {problem && <p className="text-danger mt-2 text-xs">{problem}</p>}

                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                  <label className="text-muted flex min-h-11 items-center gap-2 text-xs">
                    <input
                      type="radio"
                      name="handover-agent-default"
                      checked={agent.id === standardId}
                      onChange={() =>
                        change((current) =>
                          current.map((entry) => ({ ...entry, isDefault: entry.id === agent.id })),
                        )
                      }
                      className="accent-brand size-4"
                    />
                    Standard
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      change((current) => current.filter((entry) => entry.id !== agent.id))
                    }
                    aria-label={`${agent.name.trim() || `Makler ${index + 1}`} entfernen`}
                    className="text-muted hover:text-danger min-h-11 px-2 text-xs underline underline-offset-4 transition-colors"
                  >
                    Entfernen
                  </button>
                </div>
              </fieldset>
            )
          })}

          {draft.length < 20 && (
            <button
              type="button"
              disabled={busy}
              onClick={() => change((current) => [...current, blankAgent(current.length === 0)])}
              className="border-line text-muted hover:border-brand hover:text-brand min-h-11 border border-dashed px-3 text-xs transition-colors disabled:opacity-40"
            >
              + Makler hinzufügen
            </button>
          )}

          <div className="border-line flex flex-wrap items-center gap-x-4 gap-y-2 border-t pt-4">
            <button
              type="submit"
              disabled={!dirty || busy || invalid}
              className="bg-brand text-surface hover:bg-brand-strong px-4 py-2.5 text-xs font-medium transition-colors disabled:opacity-40"
            >
              {busy ? 'wird gespeichert …' : 'Speichern'}
            </button>

            {dirty && (
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  if (server) setDraft(server.map((agent) => ({ ...agent })))
                }}
                className="text-muted hover:text-ink text-xs underline underline-offset-4 transition-colors disabled:opacity-40"
              >
                Verwerfen
              </button>
            )}

            {saved && !dirty && <span className="text-positive text-xs">Gespeichert.</span>}
          </div>

          {save.isError && <p className="text-danger text-xs">{errorMessage(save.error)}</p>}
        </form>
      )}
    </Card>
  )
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) return error.message
  if (error instanceof Error) return error.message
  return 'Unbekannter Fehler'
}
